/**
 * `ConsentFlow` — orchestrator that drives the SHT-mediated third-
 * party-app consent flow per ADR-027.
 *
 * Composition contract:
 *
 *   IThirdPartyAppRegistryClient — verified app metadata
 *                                  (registered identity + tier +
 *                                   purpose + trust cues per
 *                                   ADR-027 §1 step 3 + §3 admission
 *                                   discipline)
 *   IConsentArtifactClient        — Consent Artifact issuance
 *                                  (`issueAppConsentArtifact` per
 *                                   ADR-027 §1 step 5; under
 *                                   Patient-Directed Use Authority)
 *
 * After step 5 (artifact issued), the orchestrator constructs the
 * step-6 hand-back URL by appending an opaque artifact reference to
 * the app-supplied `handbackUrl`. The actual redirect is performed
 * by the view layer on `granted` / `denied` terminal — the
 * orchestrator emits the URL through `getHandbackUrl()` rather than
 * driving navigation itself.
 *
 * **Doctrinal posture (load-bearing):**
 *
 *   - The orchestrator does NOT advance past `confirmation` without
 *     an explicit `grant` or `deny` event. ADR-027 §1 step 4 commits
 *     to "explicit affirmative action." Silence / dismissal /
 *     navigation-away are not grants. The orchestrator's
 *     `cancel(reason)` is a separate no-action terminal that does
 *     NOT issue an artifact.
 *   - The verified registered app metadata replaces app-claimed
 *     identity at `app-metadata-loaded`; downstream renders use the
 *     verified `displayName` / `publisherName`.
 *   - The Consent Artifact records the patient's *actual* grant
 *     scope per ADR-027 §1 step 5 — narrowed if the patient narrowed
 *     at scope-review or retention-review.
 */
import { applyEvent, initialConsentState, isTerminal, } from './consent-state.js';
import { validateHandbackUrl } from './validate-handback-url.js';
// ---------- Implementation ----------
export class ConsentFlow {
    options;
    state;
    observers = new Set();
    constructor(options) {
        this.options = options;
        this.state = initialConsentState(options.initiation);
    }
    getState() {
        return this.state;
    }
    subscribe(observer) {
        this.observers.add(observer);
        observer(this.state);
        return () => {
            this.observers.delete(observer);
        };
    }
    async loadAppMetadata() {
        if (this.state.kind !== 'loading-app-metadata') {
            return;
        }
        const handbackValidation = validateHandbackUrl(this.state.initiation.handbackUrl);
        if (!handbackValidation.ok) {
            this.transition({
                kind: 'error',
                code: 'invalid-handback-url',
                message: handbackValidation.message,
            });
            return;
        }
        try {
            const app = await this.options.registry.getRegisteredApp(this.state.initiation.appClaimedIdentity);
            if (app === null) {
                this.transition({
                    kind: 'error',
                    code: 'app-not-found',
                    message: `app ${this.state.initiation.appClaimedIdentity} is not registered`,
                });
                return;
            }
            // PL5 Constitutional Tier A severity — registered-allowlist enforcement
            // canonical per CTO 2026-05-12 third-pass security architecture re-scan
            // §1.5 (one-line breaks KC-5 harvest leg unilaterally per CTO auditor
            // highest-leverage observation). Threads `app.registeredHandbackUrls`
            // through `validateHandbackUrl(handbackUrl, { registeredHandbackUrls })`
            // post app-metadata load. When `app.registeredHandbackUrls` is
            // `undefined` (v0 pre-DEV2 substrate-types extension default),
            // allowlist check is skipped (pre-extension behavior canonical
            // preserved); when defined, exact-origin match canonical against the
            // registered allowlist.
            // Conditional spread for the optional `registeredHandbackUrls` slot
            // (exactOptionalPropertyTypes): omit when undefined — the v0
            // pre-extension default — rather than passing explicit `undefined`
            // through `ValidateHandbackUrlOptions`. Allowlist check is skipped
            // when the field is absent (pre-extension behavior preserved per the
            // existing comment above).
            const handbackAllowlistValidation = validateHandbackUrl(this.state.initiation.handbackUrl, app.registeredHandbackUrls !== undefined
                ? { registeredHandbackUrls: app.registeredHandbackUrls }
                : {});
            if (!handbackAllowlistValidation.ok) {
                this.transition({
                    kind: 'error',
                    code: 'invalid-handback-url',
                    message: handbackAllowlistValidation.message,
                });
                return;
            }
            this.transition({ kind: 'app-metadata-loaded', app });
        }
        catch (e) {
            this.transition({
                kind: 'error',
                code: 'app-metadata-load-failed',
                message: errorMessage(e),
            });
        }
    }
    continueFromAppIdentity() {
        if (this.state.kind !== 'app-identity-review') {
            return;
        }
        this.transition({ kind: 'continue-from-app-identity-review' });
    }
    narrowScope(narrowing) {
        if (this.state.kind !== 'scope-review') {
            return;
        }
        this.transition({
            kind: 'narrow-scope',
            dataScope: narrowing.dataScope,
            operationalScope: narrowing.operationalScope,
        });
    }
    continueFromScopeReview() {
        if (this.state.kind !== 'scope-review') {
            return;
        }
        this.transition({ kind: 'continue-from-scope-review' });
    }
    narrowRetention(narrowing) {
        if (this.state.kind !== 'retention-review') {
            return;
        }
        this.transition({
            kind: 'narrow-retention',
            retentionDiscipline: narrowing.retentionDiscipline,
            timeBounds: narrowing.timeBounds,
        });
    }
    continueFromRetentionReview() {
        if (this.state.kind !== 'retention-review') {
            return;
        }
        this.transition({ kind: 'continue-from-retention-review' });
    }
    continueFromTierDisclosure() {
        if (this.state.kind !== 'tier-disclosure') {
            return;
        }
        this.transition({ kind: 'continue-from-tier-disclosure' });
    }
    async grant() {
        if (this.state.kind !== 'confirmation') {
            return;
        }
        const initiation = this.state.initiation;
        const app = this.state.app;
        const narrowing = this.state.narrowing;
        this.transition({ kind: 'grant' });
        try {
            const artifact = await this.options.consentArtifact.issueAppConsentArtifact({
                subjectPci: this.options.subjectPci,
                appIdentity: app.appIdentity,
                statedPurpose: initiation.requestedPurpose,
                dataScope: narrowing.dataScope,
                operationalScope: narrowing.operationalScope,
                timeBounds: narrowing.timeBounds,
                retentionDiscipline: narrowing.retentionDiscipline,
                ...(initiation.locale !== undefined ? { locale: initiation.locale } : {}),
            });
            this.transition({ kind: 'artifact-issued', artifact });
        }
        catch (e) {
            this.transition({
                kind: 'error',
                code: 'artifact-issuance-failed',
                message: errorMessage(e),
            });
        }
    }
    deny() {
        if (this.state.kind !== 'confirmation') {
            return;
        }
        this.transition({ kind: 'deny' });
    }
    cancel(reason) {
        if (isTerminal(this.state)) {
            return;
        }
        this.transition({ kind: 'cancel', reason });
    }
    getHandbackUrl() {
        if (this.state.kind === 'granted') {
            return appendQuery(this.state.initiation.handbackUrl, {
                outcome: 'granted',
                artifactRef: this.state.artifact.artifactId,
            });
        }
        if (this.state.kind === 'denied') {
            return appendQuery(this.state.initiation.handbackUrl, { outcome: 'denied' });
        }
        return null;
    }
    // ---------- private ----------
    transition(event) {
        const next = applyEvent(this.state, event);
        if (next === this.state) {
            return;
        }
        this.state = next;
        for (const observer of this.observers) {
            observer(next);
        }
    }
}
function appendQuery(url, params) {
    const sep = url.includes('?') ? '&' : '?';
    const qs = Object.entries(params)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('&');
    return `${url}${sep}${qs}`;
}
function errorMessage(e) {
    if (e instanceof Error) {
        return e.message;
    }
    return String(e);
}
//# sourceMappingURL=consent-flow.js.map